import { Button, Stack } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MarineFunctionalGroupEnum,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  SpeciesEnum,
} from '@/api/rest/resources/types/fact';
import { GradientScale } from '@/components/widgets/o/Gradient/Gradient';

type GradientFacts = {
  min: number | null | undefined;
  max: number | null | undefined;
  value: number | null | undefined;
};

type GradientPlotReportingFacts = {
  min: PlotReportFactElement<number | null>[] | undefined;
  max: PlotReportFactElement<number | null>[] | undefined;
  value: PlotReportFactElement<number | null>[] | undefined;
};

export type SpeciesGradientGraphTileProps = {
  shootDensityFacts: GradientFacts;
  seagrassProductivityFacts: GradientFacts;
  nurseryHabitatFacts: GradientFacts;
  biomassFacts: GradientPlotReportingFacts;
  diversityFacts: GradientPlotReportingFacts;
  species: SpeciesEnum;
};

export const SpeciesGradientGraphTile = ({
  shootDensityFacts,
  seagrassProductivityFacts,
  nurseryHabitatFacts,
  biomassFacts,
  diversityFacts,
  species,
}: {
  shootDensityFacts: GradientFacts;
  seagrassProductivityFacts: GradientFacts;
  nurseryHabitatFacts: GradientFacts;
  biomassFacts: GradientPlotReportingFacts;
  diversityFacts: GradientPlotReportingFacts;
  species: SpeciesEnum;
}) => {
  const { t } = useTranslation();

  const [biomassGroup, setBiomassGroup] = useState<MarineFunctionalGroupEnum>(MarineFunctionalGroupEnum.fish);
  const [diversityGroup, setDiversityGroup] = useState<MarineFunctionalGroupEnum>(MarineFunctionalGroupEnum.fish);

  const displaySpecies = species.substring(0, 1).toUpperCase() + species.substring(1);

  const biomassFactsByGroup = getDataForFunctionalGroup(biomassFacts, biomassGroup);
  const diversityFactsByGroup = getDataForFunctionalGroup(diversityFacts, diversityGroup);

  return (
    <Stack spacing={4} className='py-8'>
      {shootDensityFacts.value && (
        <GradientScale
          {...shootDensityFacts}
          title={t('shared.ncaDetail.details.speciesGradient.labels.shootDensity')}
          tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.shootDensity', { species: displaySpecies })}
          unit={t('global.units.shoots/m^2')}
        />
      )}
      {seagrassProductivityFacts.value && (
        <GradientScale
          {...seagrassProductivityFacts}
          title={t('shared.ncaDetail.details.speciesGradient.labels.seagrassProductivity')}
          tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.seagrassProductivity', {
            species: displaySpecies,
          })}
          unit={t('global.units.mm/year')}
        />
      )}
      {nurseryHabitatFacts.value && (
        <GradientScale
          {...nurseryHabitatFacts}
          title={t('shared.ncaDetail.details.speciesGradient.labels.nurseryHabitat')}
          tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.nurseryHabitat')}
          unit={t('global.units.%')}
        />
      )}
      {biomassFactsByGroup.value && (
        <Stack>
          <GradientScale
            {...biomassFactsByGroup}
            title={t('shared.ncaDetail.details.speciesGradient.labels.biomass')}
            tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.biomass')}
            unit={t('global.units.kg/100m^2')}
          />
          <GroupSelector selectedGroup={biomassGroup} onSelectGroup={setBiomassGroup} />
        </Stack>
      )}
      {diversityFactsByGroup.value && (
        <Stack>
          <GradientScale
            {...diversityFactsByGroup}
            title={t('shared.ncaDetail.details.speciesGradient.labels.diversity')}
            tooltip={t('shared.ncaDetail.details.speciesGradient.explainer.diversity')}
            unit={t('global.units.shannon')}
          />
          <GroupSelector selectedGroup={diversityGroup} onSelectGroup={setDiversityGroup} />
        </Stack>
      )}
    </Stack>
  );
};

const getDataForFunctionalGroup = (
  facts: GradientPlotReportingFacts,
  group: MarineFunctionalGroupEnum,
): GradientFacts => {
  return {
    min: facts.min?.find((fact) =>
      fact.labels.some((label) => label.name === PlotReportFactLabelNameEnum.functional_group && label.value === group),
    )?.value,
    max: facts.max?.find((fact) =>
      fact.labels.some((label) => label.name === PlotReportFactLabelNameEnum.functional_group && label.value === group),
    )?.value,
    value: facts.value?.find((fact) =>
      fact.labels.some((label) => label.name === PlotReportFactLabelNameEnum.functional_group && label.value === group),
    )?.value,
  };
};

const GroupSelector = ({
  selectedGroup,
  onSelectGroup,
}: {
  selectedGroup: MarineFunctionalGroupEnum;
  onSelectGroup: (group: MarineFunctionalGroupEnum) => void;
}) => {
  const { t } = useTranslation();
  const groups = Object.values(MarineFunctionalGroupEnum);

  return (
    <Stack direction='row' className='mt-2' spacing={2}>
      {groups.map((group) => (
        <Button
          variant='text'
          className={`w-fit ${selectedGroup === group ? 'bg-neutral-black-10' : 'bg-bg-light-grey'}`}
          size='small'
          key={group}
          onClick={() => onSelectGroup(group)}
        >
          <span
            className={`typography-body2 font-normal ${selectedGroup === group ? 'text-primary-100' : 'text-text-secondary'}`}
          >
            {t(`global.marineFunctionalGroup.${group}`)}
          </span>
        </Button>
      ))}
    </Stack>
  );
};
